import React, { useState, useEffect } from "react";
import "./App.css";
import Login from "./authPages/Login";
import AdminDashboard from "./DashBoardPage/AdminDashboard";
import ForgotPassword from "./authPages/ForgotPassword";
import ResetPassword from "./authPages/ResetPassword";
import UserManagement from "./AdminUserManagement/UserManagement";
import ChallengeManagementList from "./challenge/ChallengeManagementList";
import ChallengeForm from "./challenge/ChallengeForm";
import ChallengeDetail from "./challenge/ChallengeDetail";
import AuditLogViewer from "./AuditLogViewer/AuditLogViewer";
import AppUserManagement from "./AppUserManagement/AppUserManagement";
import KYCApprovalQueue from "./KycManagement/KYCApprovalQueue";
import WithdrawalApprovalQueue from "./WithdrawalApprovalQueue/WithdrawalApprovalQueue";
import BannerManagement from "./BannerManagement/BannerManagement";
import ChallengeCreateForm from "./challenge/ChallengeCreateForm";
import StockManagement from "./StockManagement/StockManagement";
import StockUniverseManagement from "./StockUniverseManagement/StockUniverseManagement";
import { challengeService } from "./services/challengeService";
import { toast } from "react-toastify";
import CryptoManagement from "./CryptoManagement/CryptoManagement";
import CryptoUniverseManagement from "./CryptoUniverseManagement/CryptoUniverseManagement";
import ChallengeCloneForm from "./challenge/ChallengeCloneForm";
import SportsLegalDocs from "./sports&LegalDocs/SportsLegalDocs";
import TermPolicyDoc from "./sports&LegalDocs/TermPolicyDocs";

type AppState =
  | "login"
  | "dashboard"
  | "forgot-password"
  | "reset-password"
  | "user-management"
  | "app-user-management"
  | "kyc-management"
  | "withdrawal-management"
  | "challenge-management"
  | "challenge-create"
  | "challenge-clone"
  | "challenge-edit"
  | "challenge-view"
  | "audit-logs"
  | "banner-management"
  | "stock-management"
  | "stock-universe-management"
  | "crypto-management"
  | "crypto-universe-management"
  | "help-support"
  | "term-policy";

// Main App component for the Admin Panel
// This component will contain the login form with TPL branding and professional design.
export default function App() {
  const [currentState, setCurrentState] = useState<AppState>("login");
  const [user, setUser] = useState<any>(null);
  const [challengeData, setChallengeData] = useState<any>(null);

  // Check for existing session on app load
  useEffect(() => {
    const savedToken = localStorage.getItem("adminToken");
    const savedUser = localStorage.getItem("adminUser");
    const tokenExpiry = localStorage.getItem("tokenExpiry");

    // Check if token is expired
    if (tokenExpiry) {
      const expiryDate = new Date(tokenExpiry);
      const now = new Date();
      if (now > expiryDate) {
        console.log("Token expired, clearing session");
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        localStorage.removeItem("tokenExpiry");
        setCurrentState("login");
        return;
      }
    }

    if (savedToken && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setCurrentState("dashboard");
      } catch (error) {
        console.error("Error parsing saved user data:", error);
        localStorage.removeItem("adminToken");
        localStorage.removeItem("adminUser");
        localStorage.removeItem("tokenExpiry");
      }
    }

    // Check if we're on a reset password page
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get("token");
    if (resetToken) {
      setCurrentState("reset-password");
    }
  }, []);
  const handleLoginSuccess = (userData: any, token: string) => {
    console.log("Login successful, navigating to dashboard");
    setUser(userData);
    setCurrentState("dashboard");
  };

  const handleLogout = () => {
    setUser(null);
    setChallengeData(null);
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("tokenExpiry");
    setCurrentState("login");
  };

  const handleForgotPassword = () => {
    setCurrentState("forgot-password");
  };

  const handleBackToLogin = () => {
    setCurrentState("login");
    // Clear URL parameters if any
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const handleBackToDashboard = () => {
    setCurrentState("dashboard");
  };

  const handleNavigateToUserManagement = () => {
    setCurrentState("user-management");
  };

  const handleNavigateToAppUserManagement = () => {
    setCurrentState("app-user-management");
  };

  const handleNavigateToChallengeManagement = () => {
    setCurrentState("challenge-management");
  };

  const handleNavigateToChallengeCreate = () => {
    setCurrentState("challenge-create");
  };

  const handleNavigateToChallengeEdit = (data: any) => {
    setChallengeData(data);
    setCurrentState("challenge-edit");
  };
  const handleNavigateToChallengeClone = (data: any) => {
    setChallengeData(data);
    setCurrentState("challenge-clone");
  };

  const handleNavigateToChallengeView = (data: any) => {
    setChallengeData(data);
    setCurrentState("challenge-view");
  };

  const handleNavigateToAuditLogs = () => {
    setCurrentState("audit-logs");
  };

  const handleNavigateToKYCManagement = () => {
    setCurrentState("kyc-management");
  };

  const handleNavigateToWithdrawalManagement = () => {
    setCurrentState("withdrawal-management");
  };

  const handleNavigateToBannerManagement = () => {
    setCurrentState("banner-management");
  };
  // bvhjb
  const handleNavigateToStockManagement = () => {
    setCurrentState("stock-management");
  };
  const handleNavigateToCryptokManagement = () => {
    setCurrentState("crypto-management");
  };

  const handleNavigateToCryptoUniverseManagement = () => {
    setCurrentState("crypto-universe-management");
  };

  const handleNavigateToStockUniverseManagement = () => {
    setCurrentState("stock-universe-management");
  };

  const handleNavigateToSportsLegalDocs = () => {
    setCurrentState("term-policy");
  };

  const handleNavigateToHelpSupport = () => {
    setCurrentState("help-support");
  };

  const handleChallengeCreated = (data: any, publish: boolean = false) => {
    console.log("Challenge created:", data, "Publish:", publish);
    setCurrentState("challenge-management");
  };

  const handleChallengeUpdated = async (
    data: any,
    publish: boolean = false
  ) => {
    try {
      console.log("Updating challenge:", data, "Publish:", publish);

      // Call the API to update the challenge
      const updatedChallenge = await challengeService.updateChallenge(
        challengeData.id,
        data
      );
      console.log("Challenge updated successfully:", updatedChallenge);

      // Show success message (you can add a toast notification here)
      toast.success("Challenge updated successfully!");

      // Navigate back to challenge management
      setCurrentState("challenge-management");
    } catch (error: any) {
      console.error("Error updating challenge:", error);

      // Extract the specific error message from the backend
      let errorMessage = "Failed to update challenge. Please try again.";

      if (error.message) {
        errorMessage = error.message;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (typeof error === "string") {
        errorMessage = error;
      }

      alert(`Error: ${errorMessage}`);
    }
  };

  // Render based on current state
  switch (currentState) {
    case "dashboard":
      return (
        <AdminDashboard
          user={user}
          onLogout={handleLogout}
          onNavigateToUserManagement={handleNavigateToUserManagement}
          onNavigateToAppUserManagement={handleNavigateToAppUserManagement}
          onNavigateToChallengeManagement={handleNavigateToChallengeManagement}
          onNavigateToAuditLogs={handleNavigateToAuditLogs}
          onNavigateToKYCManagement={handleNavigateToKYCManagement}
          onNavigateToWithdrawalManagement={
            handleNavigateToWithdrawalManagement
          }
          onNavigateToBannerManagement={handleNavigateToBannerManagement}
          onNavigateToStockManagement={handleNavigateToStockManagement}
          onNavigateToStockUniverseManagement={
            handleNavigateToStockUniverseManagement
          }
          onNavigateToCryptoManagement={handleNavigateToCryptokManagement}
          onNavigateToCryptoUniverseManagement={
            handleNavigateToCryptoUniverseManagement
          }
          onNavigateToSportsLegalDocs={handleNavigateToSportsLegalDocs}
          onNavigateToHelpSupport={handleNavigateToHelpSupport}
        />
      );

    case "user-management":
      return <UserManagement onBackToDashboard={handleBackToDashboard} />;

    case "app-user-management":
      return <AppUserManagement onBackToDashboard={handleBackToDashboard} />;

    case "challenge-management":
      return (
        <ChallengeManagementList
          onBackToDashboard={handleBackToDashboard}
          onCreateChallenge={handleNavigateToChallengeCreate}
          onEditChallenge={handleNavigateToChallengeEdit}
          onViewChallenge={handleNavigateToChallengeView}
          onCloneChallenge={handleNavigateToChallengeClone}
        />
      );

    case "challenge-create":
      return (
        <ChallengeCreateForm
          onBackToChallengeList={() => setCurrentState("challenge-management")}
          onChallengeCreated={() => setCurrentState("challenge-management")}
        />
      );

    case "challenge-edit":
      return (
        <ChallengeForm
          challenge={challengeData}
          onCancel={() => setCurrentState("challenge-management")}
          onSubmit={handleChallengeUpdated}
        />
      );

    case "challenge-clone":
      return (
        <ChallengeCloneForm
          challenge={challengeData}
          onCancel={() => setCurrentState("challenge-management")}
          onChallengeCreated={() => setCurrentState("challenge-management")}
        />
      );
    case "challenge-view":
      return (
        <ChallengeDetail
          challengeData={challengeData}
          onBack={() => setCurrentState("challenge-management")}
          onEdit={() => handleNavigateToChallengeEdit(challengeData)}
          onDelete={() => {
            // Handle delete and return to challenge management
            setCurrentState("challenge-management");
          }}
        />
      );

    case "forgot-password":
      return <ForgotPassword onBackToLogin={handleBackToLogin} />;

    case "reset-password":
      return <ResetPassword onBackToLogin={handleBackToLogin} />;

    case "audit-logs":
      return <AuditLogViewer onBackToDashboard={handleBackToDashboard} />;

    case "kyc-management":
      return (
        <KYCApprovalQueue
          onBackToDashboard={handleBackToDashboard}
          onNavigateToUserManagement={handleNavigateToAppUserManagement}
        />
      );

    case "withdrawal-management":
      return (
        <WithdrawalApprovalQueue onBackToDashboard={handleBackToDashboard} />
      );

    case "banner-management":
      return <BannerManagement onBack={handleBackToDashboard} />;

    case "stock-management":
      return <StockManagement onBackToDashboard={handleBackToDashboard} />;

    case "crypto-management":
      return <CryptoManagement onBackToDashboard={handleBackToDashboard} />;

    case "stock-universe-management":
      return (
        <StockUniverseManagement onBackToDashboard={handleBackToDashboard} />
      );

    case "crypto-universe-management":
      return (
        <CryptoUniverseManagement onBackToDashboard={handleBackToDashboard} />
      );

    case "help-support":
      return <SportsLegalDocs onBackToDashboard={handleBackToDashboard} />;
    case "term-policy":
      return <TermPolicyDoc onBackToDashboard={handleBackToDashboard} />;
    case "login":
    default:
      return (
        <Login
          onLoginSuccess={handleLoginSuccess}
          onForgotPassword={handleForgotPassword}
        />
      );
  }
}
